import type { Logger } from "@/lib/logger";
import { FullscriptApiError, FullscriptUnlinkedError } from "./client";
import { readMissingScope } from "./error-summary";

/** What the provider sees when their Fullscript token lacks a scope. Only a reconnect fixes it. */
export const FULLSCRIPT_SCOPE_RECONNECT_MESSAGE =
  "Your Fullscript connection is missing a permission. Reconnect Fullscript from the Supplements tab, then retry.";

/**
 * Why Fullscript answered 403.
 *
 * scope_missing: the body is the documented `Must have `<scope>` scope enabled.` message. Every
 * 403 in Fullscript's API reference is this case. A reconnect grants the scope only when our app
 * holds it in the Fullscript API Dashboard.
 *
 * forbidden_other: any other 403 body. Fullscript does not document a status or a body for a
 * restricted endpoint without commercial approval, so that can be this case.
 */
export type FullscriptForbiddenReason =
  | { kind: "scope_missing"; scope: string }
  | { kind: "forbidden_other" };

/** Classifies a Fullscript 403. Null for any other error, so the caller keeps its old branches. */
export function classifyFullscriptForbidden(error: unknown): FullscriptForbiddenReason | null {
  if (!(error instanceof FullscriptApiError) || error.status !== 403) return null;
  if (error.missingScope) return { kind: "scope_missing", scope: error.missingScope };
  return { kind: "forbidden_other" };
}

/**
 * Maps a scope 403 to the reconnect prompt. `FullscriptUnlinkedError` is an `ExpectedActionError`,
 * so createServerAction sends no Sentry event for it. Any other error comes back unchanged, so the
 * caller rethrows it as before.
 */
export function toScopeReconnectError({
  error,
  logger,
  context,
}: {
  error: unknown;
  logger: Pick<Logger, "warn">;
  context: Record<string, unknown>;
}): unknown {
  const forbidden = classifyFullscriptForbidden(error);
  if (forbidden?.kind !== "scope_missing") return error;
  // fixed by a reconnect, not by our code - scope name only, no Sentry event
  logger.warn(
    "Fullscript connection is missing a scope",
    { ...context, status: 403, scope: forbidden.scope },
    { mirrorToSentry: false },
  );
  return new FullscriptUnlinkedError(FULLSCRIPT_SCOPE_RECONNECT_MESSAGE);
}

/**
 * The `FullscriptApiError` for a non-2xx response. Reads the body of a 403 only, and keeps only
 * the scope name from it: other bodies can echo patient data or invitation URLs.
 */
export async function readFullscriptApiError(response: Response): Promise<FullscriptApiError> {
  const missingScope =
    response.status === 403 ? readMissingScope(await response.text().catch(() => "")) : undefined;
  return new FullscriptApiError(
    `Fullscript request failed (${response.status}).`,
    response.status,
    { missingScope },
  );
}
